// /Users/pramod/Desktop/school-erp/frontend/src/Tenant/TenantWebsite/sections/Events.js

import React from 'react';
import { FiCalendar, FiClock, FiMapPin, FiUsers, FiExternalLink } from 'react-icons/fi';
import buildResolvedFile from '../utils/buildResolvedFile';
import useBrandColors from '../utils/useBrandColors';

// Helper function to resolve the event image source safely
const getImage = (item, apiBaseURL) => {
  const candidate = item.image || item.imageUrl || item.photo || item.banner || '';
  return buildResolvedFile(candidate, apiBaseURL).resolvedSrc || '';
};

// Event Card Component
const EventCard = ({ item, apiBaseURL }) => {
  const eventImg = getImage(item, apiBaseURL);
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();
  
  const eventDate = item.date ? new Date(item.date) : null;
  const formattedDate = eventDate ? eventDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }) : null;

  return (
    <div 
      className="group bg-white rounded-2xl overflow-hidden border transition-all duration-500 hover:shadow-2xl hover:-translate-y-1"
      style={{ borderColor: `${primary}15` }}
    >
      {/* Image Container (Reduced by 30% using w-[70%] and centered) */}
      <div className="pt-5 px-5">
        <div 
          className="relative w-[70%] mx-auto aspect-[16/9] overflow-hidden rounded-xl"
          style={{ 
            background: `linear-gradient(135deg, ${primary}10, ${secondary}10)`
          }}
        >
          {eventImg ? (
            <img
              src={eventImg}
              alt={item.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <FiCalendar className="w-10 h-10" style={{ color: secondary }} />
            </div>
          )}
          
          {/* Free Badge */}
          {item.isFree && (
            <div 
              className="absolute top-2 right-2 px-2.5 py-1 text-white text-[10px] font-medium rounded-full"
              style={{ backgroundColor: secondary }}
            >
              Free
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6" style={{ fontFamily: fontBody }}>
        <h4 
          className="text-lg font-bold mb-2"
          style={{ 
            color: '#1F2937',
            fontFamily: fontHeading,
          }}
        >
          {item.title}
        </h4>

        {item.description && (
          <p className="text-sm leading-relaxed mb-4 line-clamp-2" style={{ color: '#6B7280' }}>
            {item.description}
          </p>
        )}

        {/* Details */}
        <div className="space-y-2 text-sm">
          {formattedDate && (
            <div className="flex items-center gap-2" style={{ color: '#6B7280' }}>
              <FiCalendar className="w-4 h-4 flex-shrink-0" style={{ color: secondary }} />
              <span>{formattedDate}</span>
            </div>
          )}
          
          {item.time && (
            <div className="flex items-center gap-2" style={{ color: '#6B7280' }}>
              <FiClock className="w-4 h-4 flex-shrink-0" style={{ color: secondary }} />
              <span>{item.time}</span>
            </div>
          )}
          
          {item.venue && (
            <div className="flex items-center gap-2" style={{ color: '#6B7280' }}>
              <FiMapPin className="w-4 h-4 flex-shrink-0" style={{ color: secondary }} />
              <span>{item.venue}</span>
            </div>
          )}
        </div>

        {/* Speakers */}
        {item.speakers && Array.isArray(item.speakers) && item.speakers.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            <FiUsers className="w-4 h-4 mt-0.5" style={{ color: secondary }} />
            {item.speakers.slice(0, 2).map((speaker, idx) => (
              <span key={idx} className="text-xs" style={{ color: '#6B7280' }}>
                {speaker}
                {idx < Math.min(item.speakers.length - 1, 1) && ','}
              </span>
            ))}
            {item.speakers.length > 2 && (
              <span className="text-xs" style={{ color: '#9CA3AF' }}>
                +{item.speakers.length - 2} more
              </span>
            )}
          </div>
        )}

        {/* Register Link */}
        {item.registrationLink && (
          <a
            href={item.registrationLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm transition-colors mt-4 group/link"
            style={{ color: secondary }}
          >
            <span>Register Now</span>
            <FiExternalLink className="w-3 h-3 transition-transform group-hover/link:translate-x-0.5" />
          </a>
        )}
      </div>
    </div>
  );
};

// Main Events Component
const Events = ({ events, apiBaseURL = '' }) => {
  const { sectionTitle, sectionSubtitle, displayStyle = 'card', items = [] } = events || {};
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  if (!items || items.length === 0) return null;

  const gridClasses = {
    'card': 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6',
    'list': 'grid-cols-1 max-w-4xl mx-auto gap-4',
    'grid': 'grid-cols-1 md:grid-cols-2 gap-6',
  };

  return (
    <section id="events" className="py-16 md:py-24 bg-gray-50/30">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          {(sectionTitle || sectionSubtitle) && (
            <div className="text-center mb-12 md:mb-16">
              <span 
                className="inline-block text-xs font-light tracking-[0.2em] uppercase mb-3"
                style={{ color: secondary }}
              >
                Events
              </span>
              {sectionTitle && (
                <h2 
                  className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3"
                  style={{ 
                    color: primary,
                    fontFamily: fontHeading,
                  }}
                >
                  {sectionTitle}
                </h2>
              )}
              {sectionSubtitle && (
                <p 
                  className="text-lg font-light max-w-2xl mx-auto"
                  style={{ 
                    color: '#6B7280',
                    fontFamily: fontBody,
                  }}
                >
                  {sectionSubtitle}
                </p>
              )}
              <div className="mt-6 flex justify-center">
                <div 
                  className="h-1 w-20 rounded-full" 
                  style={{ 
                    background: `linear-gradient(90deg, ${primary}, ${secondary})` 
                  }} 
                />
              </div>
            </div>
          )}

          {/* Events Grid */}
          <div className={gridClasses[displayStyle] || gridClasses['card']}>
            {items.map((item) => (
              <EventCard 
                key={item._id || item.id || item.title} 
                item={item} 
                apiBaseURL={apiBaseURL}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Events;